import { faceNotation, type LinkPreview, type MatchView, type ReplayCheck } from '../../engine';
import { t } from '../../i18n';
import { patternName, pawnRef, routeWords, type Names } from '../../app/describe';
import { RouteGlyph } from './RouteGlyph';

/** Live preview of a link before it is committed (§19.6) and its capture consequences (§19.7). */
export function PreviewPanel({
  v,
  preview,
  names,
  flipRoute,
}: {
  v: MatchView;
  preview: LinkPreview;
  names: Names;
  flipRoute: boolean;
}) {
  const b = preview.breakdown;
  return (
    <section className="preview-panel" aria-live="polite" aria-label={t('preview.title')}>
      <h3>
        <code>{preview.step.notation}</code>
      </h3>
      <p className="preview-formula">
        <span className="mini-label">{t('preview.ifEnded')}</span>
        <strong>
          {t('preview.formula', { p: b.points.total, m: b.multiplier.total, score: b.score })}
        </strong>
      </p>
      <ul className="preview-lines">
        <li>
          {t('preview.points', {
            landing: b.points.landing,
            capture: b.points.capture,
            crown: b.points.crown,
          })}
        </li>
        <li>
          {t('preview.multiplier', {
            links: b.multiplier.links,
            pattern: b.multiplier.pattern
              ? `${b.multiplier.patternBonus} (${patternName(b.multiplier.pattern)})`
              : b.multiplier.patternBonus,
            fullCircle: b.multiplier.fullCircleBonus,
          })}
        </li>
        {b.multiplier.fullCircle && <li className="good">{t('preview.fullCircle')}</li>}
        {preview.captures.length > 0 && (
          <li className="good">{t('preview.captures', { n: preview.captures.length })}</li>
        )}
        {preview.crowned && (
          <li className="good">
            {preview.crownBonusPending ? t('preview.crownPending') : t('preview.crowns')}
          </li>
        )}
        {preview.collateralPawnId && (
          <li className="warn">
            {t('preview.collateral', { pawn: names.pawn(preview.collateralPawnId) })}
          </li>
        )}
        <li>
          {t('preview.nextTop', {
            face: faceNotation(preview.newTop.face, preview.newTop.wasWild),
          })}
        </li>
        {preview.chainEnds && <li className="dim">{t('preview.chainEnds')}</li>}
      </ul>
      {preview.captures
        .filter((c) => c.epitaph)
        .map((c) => (
          <div key={c.pawnId} className="epitaph-warning" role="note">
            <RouteGlyph links={c.epitaph!.links} size={30} flip={flipRoute} />
            <p>
              {t('preview.epitaphGift', {
                pawn: pawnRef(v, c.pawnId, names),
                owner: names.side(v.pawns[c.pawnId]?.side ?? 'S'),
                route: routeWords(c.epitaph!.links, v.pawns[c.pawnId]?.side ?? 'S'),
                score: c.epitaph!.recordedScore,
              })}
            </p>
          </div>
        ))}
    </section>
  );
}

/** An Epitaph replay's ghost route and live score for one pawn (§19.8). */
export function EpitaphPreview({
  v,
  check,
  pawn,
  names,
  flipRoute,
}: {
  v: MatchView;
  check: ReplayCheck;
  pawn: string;
  names: Names;
  flipRoute: boolean;
}) {
  if (!check.ok)
    return (
      <p className="dim">
        {t('epitaph.cannot', {
          pawn: pawnRef(v, pawn, names),
          link: check.failedAt + 1,
          reason: t(`epitaph.fail.${check.reason}`),
        })}
      </p>
    );
  const b = check.breakdown;
  return (
    <section className="preview-panel" aria-live="polite">
      <h3>
        <code>
          {check.steps.map((s) => `${faceNotation(s.link.face, s.link.wasWild)}`).join(' → ')}
        </code>
      </h3>
      <p className="preview-formula">
        <strong>
          {t('preview.formula', { p: b.points.total, m: b.multiplier.total, score: b.score })}
        </strong>
      </p>
      <ul className="preview-lines">
        <li>
          {t('preview.points', {
            landing: b.points.landing,
            capture: b.points.capture,
            crown: b.points.crown,
          })}
        </li>
        <li>
          {t('preview.multiplier', {
            links: b.multiplier.links,
            pattern: b.multiplier.pattern
              ? `${b.multiplier.patternBonus} (${patternName(b.multiplier.pattern)})`
              : b.multiplier.patternBonus,
            fullCircle: b.multiplier.fullCircleBonus,
          })}
        </li>
        {b.multiplier.fullCircle && <li className="good">{t('preview.fullCircle')}</li>}
        {check.captures.length > 0 && (
          <li className="good">{t('preview.captures', { n: check.captures.length })}</li>
        )}
        {check.crowned && (
          <li className="good">
            {v.config.crownMode === 'delayed' ? t('preview.crownPending') : t('preview.crowns')}
          </li>
        )}
        <li>{t('preview.nextTop', { face: faceNotation(check.face, false) })}</li>
      </ul>
      {check.captures.map((id) => {
        // §19.7: capturing an inscribed pawn hands its route to the opponent.
        const inscription = v.pawns[id]?.inscription;
        if (!inscription) return null;
        return (
          <div key={id} className="epitaph-warning" role="note">
            <RouteGlyph links={inscription.links} size={30} flip={flipRoute} />
            <p>
              {t('preview.epitaphGift', {
                pawn: pawnRef(v, id, names),
                owner: names.side(v.pawns[id]?.side ?? 'S'),
                route: routeWords(inscription.links, v.pawns[id]?.side ?? 'S'),
                score: inscription.recordedScore,
              })}
            </p>
          </div>
        );
      })}
    </section>
  );
}
